import { sb } from '../supabase';

// Data layer for the admin leads page (factory_leads — the imported Chinese
// company-registry directory). Leads are NOT factories: they have no products,
// no page and no claim link, so nothing here touches factory_directory.
//
// The table holds ~50MB of descriptions across 9,173 rows, so the page never
// selects it whole: it reads group counts from admin_factory_lead_tree, then
// fetches LIST_COLS for one 大类 group at a time as the admin expands it.

// Everything the row needs — deliberately excludes description_* and scope_*.
const LIST_COLS = [
  'id', 'company_name_zh', 'company_name_ar', 'legal_rep_ar', 'legal_rep_zh',
  'province_ar', 'province_zh', 'district_zh',
  'mobiles', 'phones', 'email', 'wechat', 'mobile_shared', 'has_mobile',
  'ind_l3_ar', 'ind_l3_zh', 'ind_l4_ar', 'ind_l4_zh',
  'ind_l2_ar', 'ind_l2_zh',
  'is_manufacturer', 'is_exporter', 'consumer_relevance', 'relevance_source', 'relevance_set_at',
  'status', 'contact_channel', 'contacted_at', 'note', 'factory_id',
  'opt_out_at', 'opt_out_reason',
].join(', ');

export const LEAD_STATUSES = ['new', 'contacted', 'replied', 'rejected', 'converted'];

export const STATUS_LABEL = {
  new:       { ar: 'جديد',        en: 'New' },
  contacted: { ar: 'تم التواصل',  en: 'Contacted' },
  replied:   { ar: 'ردّ',          en: 'Replied' },
  rejected:  { ar: 'رفض',         en: 'Declined' },
  converted: { ar: 'تحوّل',        en: 'Converted' },
};

// Filters shared by the tree and the row query, so a group header's count always
// matches the number of rows that open under it.
const rpcArgs = (f = {}) => ({
  p_search: f.search || null,
  p_mfg_only: !!f.mfgOnly,
  p_exporter_only: !!f.exporterOnly,
  p_mobile_only: !!f.mobileOnly,
  p_status: f.status || null,
  p_province: f.province || null,
  p_relevance: f.relevance || null,
  p_campaign: !!f.campaign,
});

// The campaign segment, spelled the same way as the partial index behind it and
// as p_campaign in admin_factory_lead_tree. Every send batch is drawn from this.
//
// has_mobile is a generated column precisely so this clause exists here at all:
// PostgREST cannot express cardinality(mobiles) > 0, and filtering it in JS after
// the rows arrive works for a list but does nothing for a head:true count — which
// is how the bar came to read 741 against a segment of 463.
function applyCampaign(q) {
  return q.eq('is_manufacturer', true)
    .eq('consumer_relevance', 'consumer')
    .eq('has_mobile', true)
    .eq('mobile_shared', false)
    .eq('status', 'new')
    .is('opt_out_at', null);
}

/** (L1, L2) group counts under the current filters. */
export async function fetchLeadTree(filters) {
  const { data, error } = await sb.rpc('admin_factory_lead_tree', rpcArgs(filters));
  if (error) throw error;
  return data || [];
}

/** Filters shared by the group query and the flat search. */
function applyFilters(q, f = {}) {
  // Campaign narrows, it does not replace: the tree RPC ANDs p_campaign with
  // every other filter, so the row query must too or a group header's count
  // stops matching the rows that open under it.
  if (f.campaign) q = applyCampaign(q);
  if (f.mfgOnly) q = q.eq('is_manufacturer', true);
  if (f.exporterOnly) q = q.eq('is_exporter', true);
  if (f.province) q = q.eq('province_zh', f.province);
  if (f.mobileOnly && !f.campaign) q = q.eq('has_mobile', true);
  // status and relevance are pinned by the campaign segment ('new' / 'consumer').
  // Re-applying a different value would AND two eq() on one column and silently
  // return nothing, so the campaign definition wins and the page disables both.
  if (!f.campaign && f.status) q = q.eq('status', f.status);
  if (!f.campaign && f.relevance) q = q.eq('consumer_relevance', f.relevance);
  return q;
}

/** Rows inside one 大类 group. `l2` is '' for the unclassified bucket. */
export async function fetchLeadsInGroup(filters, l1, l2, { limit = 200, offset = 0 } = {}) {
  const f = filters || {};
  let q = sb.from('factory_leads').select(LIST_COLS);

  // '' is how the tree reports a NULL classification — match it back to NULL.
  q = l1 ? q.eq('ind_l1_zh', l1) : q.is('ind_l1_zh', null);
  q = l2 ? q.eq('ind_l2_zh', l2) : q.is('ind_l2_zh', null);
  q = applyFilters(q, f);
  if (f.search) q = q.ilike('search_text', `%${f.search.trim()}%`);

  // Exporters first — the ranking decision, not a hard filter.
  const { data, error } = await q
    .order('is_exporter', { ascending: false })
    .order('company_name_ar', { ascending: true })
    .range(offset, offset + limit - 1);
  if (error) throw error;
  return data || [];
}

/**
 * Flat search across the whole directory — it deliberately ignores the fold, so
 * a hit in a collapsed sector is still found. Matching happens entirely in the
 * database against the generated `search_text` column (both name spellings, the
 * legal rep and the L3 activity); only the matches cross the wire.
 *
 * A needle of one or two characters cannot use the trigram index — `%xx%` has no
 * interior trigram — so it falls back to a sequential scan, ~45ms over 9,173
 * rows. That is the accepted cost of searching Chinese, where most product
 * words are exactly two characters.
 */
export async function searchLeads(filters, { limit = 200 } = {}) {
  const f = filters || {};
  const needle = (f.search || '').trim();
  if (!needle) return [];

  let q = applyFilters(sb.from('factory_leads').select(LIST_COLS), f)
    .ilike('search_text', `%${needle}%`);

  const { data, error } = await q
    .order('is_exporter', { ascending: false })
    .order('company_name_ar', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

/**
 * How many leads the campaign toggle covers. Read from the database function
 * that spells the predicate once, rather than rebuilt here — the bar is the
 * number send batches are sized against, so it must come from the same
 * definition that selects the rows, not a second copy of it.
 */
export async function countCampaignReady() {
  const { data, error } = await sb.rpc('admin_factory_lead_campaign_count');
  if (error) throw error;
  return Number(data) || 0;
}

/** Provinces for the filter dropdown, most-populated first. */
export async function fetchLeadProvinces() {
  const { data, error } = await sb.rpc('admin_factory_lead_provinces');
  if (error) throw error;
  return data || [];
}

/** Move a lead along the outreach pipeline. Admin-only via RLS. */
export async function setLeadStatus(id, status, extra = {}) {
  const patch = { status, ...extra };
  if (status === 'contacted' && !patch.contacted_at) {
    patch.contacted_at = new Date().toISOString();
  }
  const { error } = await sb.from('factory_leads').update(patch).eq('id', id);
  if (error) throw error;
}

/** Stamp an outreach attempt without moving a lead backwards. */
export async function markContacted(id, channel) {
  const { data: { user } = {} } = await sb.auth.getUser();
  const { error } = await sb.from('factory_leads').update({
    status: 'contacted',
    contact_channel: channel,
    contacted_at: new Date().toISOString(),
    contacted_by: user?.id || null,
  }).eq('id', id).eq('status', 'new');   // never demote a replied/converted lead
  if (error) throw error;
}

export async function setLeadNote(id, note) {
  const { error } = await sb.from('factory_leads').update({ note }).eq('id', id);
  if (error) throw error;
}

/**
 * The factory asked us to stop. Final and irreversible — the database refuses
 * any later change to opt_out_at — and it drops the lead out of every campaign
 * segment no matter what happens to `status` afterwards.
 */
export async function optOutLead(id, reason = '') {
  const { data: { user } = {} } = await sb.auth.getUser();
  const { error } = await sb.from('factory_leads').update({
    opt_out_at: new Date().toISOString(),
    opt_out_by: user?.id || null,
    opt_out_reason: reason || null,
    status: 'rejected',
  }).eq('id', id).is('opt_out_at', null);
  if (error) throw error;
}

/** Rahaf's review queue: unclear sectors, biggest first, with progress. */
export async function fetchUnclearSectors() {
  const { data, error } = await sb.rpc('admin_factory_lead_unclear_sectors');
  if (error) throw error;
  return data || [];
}

/** The unclear rows of one sector, plus the ones already settled there. */
export async function fetchReviewRows(l2, { limit = 200 } = {}) {
  let q = sb.from('factory_leads').select(LIST_COLS).eq('is_manufacturer', true);
  q = l2 ? q.eq('ind_l2_zh', l2) : q.is('ind_l2_zh', null);
  const { data, error } = await q
    .or('consumer_relevance.eq.unclear,relevance_source.eq.manual')
    .order('consumer_relevance', { ascending: true })   // unclear before settled
    .order('is_exporter', { ascending: false })
    .order('company_name_ar', { ascending: true })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

/**
 * Settle one unclear row by hand. The database refuses this on any row the
 * classifier already decided — automatic and human verdicts are kept apart so
 * the classifier stays measurable — and stamps relevance_source='manual' itself,
 * which also makes the row invisible to a re-classification.
 */
export async function setRelevanceManually(id, verdict) {
  const { error } = await sb.from('factory_leads')
    .update({ consumer_relevance: verdict })
    .eq('id', id).eq('consumer_relevance', 'unclear');
  if (error) throw error;
}

export const UNDO_WINDOW_MS = 24 * 60 * 60 * 1000;

/** A manual verdict can be corrected for 24 hours; the guard enforces the rest. */
export function canUndoRelevance(lead) {
  if (lead?.relevance_source !== 'manual' || !lead?.relevance_set_at) return false;
  return Date.now() - new Date(lead.relevance_set_at).getTime() < UNDO_WINDOW_MS;
}

/** Correct a manual verdict inside the window. Refused by the database after it. */
export async function correctRelevance(id, verdict) {
  const { error } = await sb.from('factory_leads')
    .update({ consumer_relevance: verdict })
    .eq('id', id).eq('relevance_source', 'manual');
  if (error) throw error;
}
